import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Pencil, Trash2, AlertTriangle, Music, DollarSign, Calculator, Loader2 } from 'lucide-react';
import api from '@/lib/axios';
import { matchesFreeText } from '@/components/search/SearchBuilder';
import { tariffFormName, formatDeNumber } from '@/lib/utils';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import DateInput from '@/components/ui/date-input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
const BILLING_TYPES = [
  { value: 'monthly', label: 'monatlich' },
  { value: 'biweekly', label: '14-tägig' },
  { value: 'weekly', label: 'wöchentlich' },
  { value: 'quarterly', label: 'quartalsweise' },
  { value: 'yearly', label: 'jährlich' },
  { value: 'one_time', label: 'einmalig' },
];

const EMPTY_TARIFF = {
  name: '',
  description: '',
  amount: '',
  billing_type: 'monthly',
  is_active: true,
  gema_applicable: false,
  adult_surcharge_amount: '',
  social_fund_amount: '',
  duration_minutes: '',
};

const EMPTY_FEE = {
  amount: '',
  billing_type: 'monthly',
  no_zero_invoices: false,
  quantity: '',
  from_month: '',
  adult_fee: '',
  adult_from_age: '18',
  valid_from: '',
  valid_until: '',
  duration_minutes: '',
};

const todayIso = () => new Date().toISOString().slice(0, 10);

function fmtMoney(v) {
  const n = parseFloat(v);
  if (Number.isNaN(n)) return '—';
  return `${formatDeNumber(n)} €`;
}

function billingLabel(type) {
  return BILLING_TYPES.find((b) => b.value === type)?.label || type || '—';
}

function fmtDate(d) {
  if (!d) return '';
  const [y, m, day] = String(d).slice(0, 10).split('-');
  if (!y || !m || !day) return d;
  return `${day}.${m}.${y}`;
}

const GEMA_FEE_EUR = 1.2;

function feeAsOfLocal(fees, asOf) {
  const d = (asOf || '').slice(0, 10);
  if (!d) return null;
  const valid = (fees || []).filter((f) => {
    const from = (f.valid_from || '').slice(0, 10);
    const until = f.valid_until ? String(f.valid_until).slice(0, 10) : null;
    if (from && from > d) return false;
    if (until && until < d) return false;
    return !!from;
  });
  valid.sort((a, b) => String(b.valid_from).localeCompare(String(a.valid_from)));
  return valid[0] || null;
}

/** Sofortige Vorschau aus Tarif-Detail (ohne API) — Jugend vs. Erwachsene. */
function buildLocalPreview(detail, asOf) {
  if (!detail) return null;
  const fee = feeAsOfLocal(detail.fees, asOf);
  const base = parseFloat(fee?.amount ?? detail.amount) || 0;
  const adultFee = parseFloat(fee?.adult_fee ?? detail.adult_surcharge_amount) || 0;
  const adultAge = Number(fee?.adult_from_age ?? 18) || 18;
  const gema = detail.gema_applicable ? GEMA_FEE_EUR : 0;
  const social = parseFloat(detail.social_fund_amount) || 0;
  const billing = fee?.billing_type || detail.billing_type || 'monthly';

  const activeGroups = (detail.discount_groups || []).filter((g) => {
    const from = g.valid_from ? String(g.valid_from).slice(0, 10) : null;
    const until = g.valid_until ? String(g.valid_until).slice(0, 10) : null;
    if (from && from > asOf) return false;
    if (until && until < asOf) return false;
    return true;
  });

  const build = (label, withAdult) => {
    const lines = withAdult && adultFee > 0
      ? [{ kind: 'base', label: 'Erwachsenengebühr', amount: base + adultFee }]
      : [{ kind: 'base', label: 'Grundgebühr', amount: base }];
    if (gema > 0) {
      lines.push({ kind: 'zuschlag', label: 'GEMA-Kopierlizenz', amount: gema, discount_free: true });
    }
    if (social > 0) {
      lines.push({ kind: 'zuschlag', label: 'Sozialfonds', amount: social, discount_free: true });
    }
    const total = lines.reduce((s, l) => s + (Number(l.amount) || 0), 0);
    return {
      label,
      lines,
      conditional_adjustments: [],
      total,
    };
  };

  return {
    tariff_id: detail.id,
    tariff_name: detail.name,
    as_of: asOf,
    billing_type: billing,
    billing_type_label: billingLabel(billing),
    fee: {
      amount: base,
      adult_fee: adultFee,
      adult_from_age: adultAge,
      valid_from: fee?.valid_from || null,
      valid_until: fee?.valid_until || null,
    },
    gema_applicable: !!detail.gema_applicable,
    gema_fee: gema,
    social_fund: social,
    discount_groups: activeGroups.map((g) => ({
      id: g.id,
      name: g.discount_group_name || g.discount_group_id,
      active_on_as_of: true,
    })),
    youth: build('Jugendliche', false),
    adult: build(`Erwachsene (ab ${adultAge} Jahren)`, true),
    _local: true,
  };
}

function PreviewScenario({ scenario }) {
  if (!scenario) return null;
  const zuschlaege = (scenario.lines || []).filter((l) => l.kind === 'zuschlag');
  const abschlaege = (scenario.lines || []).filter((l) => l.kind === 'abschlag');
  const base = (scenario.lines || []).find((l) => l.kind === 'base');

  const fmtSigned = (n) => {
    const v = Number(n) || 0;
    const abs = formatDeNumber(Math.abs(v));
    if (v > 0) return `+${abs} €`;
    if (v < 0) return `−${abs} €`;
    return `${abs} €`;
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm" data-testid="preview-scenario">
      <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
        <h4 className="text-sm font-bold text-slate-900">{scenario.label}</h4>
      </div>
      <div className="p-4 space-y-3">
        {base && (
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">{base.label}</span>
            <span className="font-semibold tabular-nums text-slate-900">{fmtMoney(base.amount)}</span>
          </div>
        )}

        <div>
          <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1.5">Zuschläge</p>
          {zuschlaege.length === 0 ? (
            <p className="text-xs text-slate-400 italic">Keine Zuschläge</p>
          ) : (
            <ul className="space-y-1.5">
              {zuschlaege.map((l, i) => (
                <li key={`z-${i}`} className="flex justify-between gap-3 text-sm">
                  <span className="text-slate-600 min-w-0">
                    {l.label}
                    {l.value_label && <span className="text-slate-400 ml-1">({l.value_label})</span>}
                    {l.discount_free && <span className="ml-1 text-[10px] text-slate-400">rabattfrei</span>}
                  </span>
                  <span className="tabular-nums font-medium shrink-0 text-brand-red">{fmtSigned(l.amount)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1.5">Abschläge</p>
          {abschlaege.length === 0 ? (
            <p className="text-xs text-slate-400 italic">Keine Abschläge</p>
          ) : (
            <ul className="space-y-1.5">
              {abschlaege.map((l, i) => (
                <li key={`a-${i}`} className="flex justify-between gap-3 text-sm">
                  <span className="text-slate-600 min-w-0">
                    {l.label}
                    {l.value_label && <span className="text-slate-400 ml-1">({l.value_label})</span>}
                  </span>
                  <span className="tabular-nums font-medium shrink-0 text-emerald-700">{fmtSigned(l.amount)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {(scenario.conditional_adjustments || []).length > 0 && (
          <div className="pt-1 border-t border-dashed border-slate-200">
            <p className="text-[10px] font-bold uppercase tracking-wide text-amber-700 mb-1.5">
              Nicht im Total — nur mit realen Schülerdaten
            </p>
            <ul className="space-y-1">
              {scenario.conditional_adjustments.map((l, i) => (
                <li key={`c-${i}`} className="text-xs text-slate-500">
                  {l.label} · {l.value_label}
                  {l.conditions?.length > 0 && (
                    <span className="block text-[10px] text-slate-400">{l.conditions.join(', ')}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex justify-between items-baseline pt-3 border-t border-slate-200">
          <span className="text-sm font-semibold text-slate-800">Monatsbetrag</span>
          <span className="text-2xl font-bold tabular-nums text-brand-red">{fmtMoney(scenario.total)}</span>
        </div>
      </div>
    </div>
  );
}

export default function TariffMasterDetail({ tariffs, loading, filter, setFilter, zeroOnly, setZeroOnly, onReload }) {
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [tariffEditor, setTariffEditor] = useState(null);
  const [tariffToDelete, setTariffToDelete] = useState(null);
  const [feeEditor, setFeeEditor] = useState(null);
  const [feeToDelete, setFeeToDelete] = useState(null);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const saveFeeBtnRef = useRef(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [previewAsOf, setPreviewAsOf] = useState(() => new Date().toISOString().slice(0, 10));
  const [simEnabled, setSimEnabled] = useState(false);
  const [simGkz, setSimGkz] = useState('GRE');
  const [simVoucher, setSimVoucher] = useState(false);
  const [simFocus, setSimFocus] = useState({ participant: 1, subject: 2 });
  const [simParticipants, setSimParticipants] = useState(() => ([
    { age: 12, subjects: [true, true, false, false] },
    { age: 10, subjects: [true, true, false, false] },
    { age: 8, subjects: [true, false, false, false] },
    { age: 15, subjects: [false, false, false, false] },
  ]));

  const filtered = useMemo(() => {
    return (tariffs || []).filter((t) => {
      if (zeroOnly && parseFloat(t.amount) > 0) return false;
      return matchesFreeText(t, filter);
    });
  }, [tariffs, filter, zeroOnly]);

  const zeroCount = (tariffs || []).filter((t) => parseFloat(t.amount) <= 0).length;

  useEffect(() => {
    if (!selectedId && filtered.length > 0) {
      setSelectedId(filtered[0].id);
    } else if (selectedId && !filtered.some((t) => t.id === selectedId) && filtered.length > 0) {
      setSelectedId(filtered[0].id);
    }
  }, [filtered, selectedId]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      setSettings(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    api.get(`/tariffs-discounts/tariffs/${selectedId}`)
      .then((r) => {
        if (cancelled) return;
        setDetail(r.data);
        setSettings({
          name: r.data.name || '',
          description: r.data.description || '',
          is_active: !!r.data.is_active,
          gema_applicable: !!r.data.gema_applicable,
          social_fund_amount: r.data.social_fund_amount != null ? String(r.data.social_fund_amount) : '0',
        });
      })
      .catch(() => {
        if (!cancelled) toast.error('Tarif konnte nicht geladen werden');
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedId]);

  const reloadDetail = async () => {
    if (!selectedId) return;
    const r = await api.get(`/tariffs-discounts/tariffs/${selectedId}`);
    setDetail(r.data);
    setSettings({
      name: r.data.name || '',
      description: r.data.description || '',
      is_active: !!r.data.is_active,
      gema_applicable: !!r.data.gema_applicable,
      social_fund_amount: r.data.social_fund_amount != null ? String(r.data.social_fund_amount) : '0',
    });
    onReload?.();
  };

  const saveNewTariff = async () => {
    const f = tariffEditor;
    if (!f.name?.trim()) { toast.error('Name ist erforderlich'); return; }
    if (f.amount === '' || f.amount === null || Number.isNaN(parseFloat(f.amount))) {
      toast.error('Bitte einen gültigen Betrag eingeben (0 ist erlaubt)');
      return;
    }
    const payload = {
      name: f.name.trim(),
      description: f.description || '',
      amount: parseFloat(f.amount),
      billing_type: f.billing_type || 'monthly',
      is_active: !!f.is_active,
      gema_applicable: !!f.gema_applicable,
      adult_surcharge_amount: f.adult_surcharge_amount === '' || f.adult_surcharge_amount == null
        ? 0 : parseFloat(f.adult_surcharge_amount) || 0,
      social_fund_amount: f.social_fund_amount === '' || f.social_fund_amount == null
        ? 0 : parseFloat(f.social_fund_amount) || 0,
      duration_minutes: f.duration_minutes === '' || f.duration_minutes == null
        ? null : parseInt(f.duration_minutes, 10) || null,
    };
    try {
      const r = await api.post('/tariffs-discounts/tariffs', payload);
      toast.success('Tarif erstellt');
      setTariffEditor(null);
      await onReload?.();
      setSelectedId(r.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const confirmDeleteTariff = async () => {
    try {
      await api.delete(`/tariffs-discounts/tariffs/${tariffToDelete.id}`);
      toast.success('Tarif gelöscht');
      setTariffToDelete(null);
      setSelectedId(null);
      onReload?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Tarif konnte nicht gelöscht werden');
    }
  };

  const saveSettings = async () => {
    if (!settings?.name?.trim()) { toast.error('Name ist erforderlich'); return; }
    setSavingSettings(true);
    try {
      await api.patch(`/tariffs-discounts/tariffs/${selectedId}/settings`, {
        name: settings.name.trim(),
        description: settings.description || '',
        is_active: !!settings.is_active,
        gema_applicable: !!settings.gema_applicable,
        social_fund_amount: parseFloat(settings.social_fund_amount || '0') || 0,
      });
      toast.success('Einstellungen gespeichert');
      await reloadDetail();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSavingSettings(false);
    }
  };

  const saveFee = async () => {
    const f = feeEditor;
    if (f.amount === '' || Number.isNaN(parseFloat(f.amount))) {
      toast.error('Bitte Gebühr eingeben');
      return;
    }
    const payload = {
      amount: parseFloat(f.amount),
      billing_type: f.billing_type || 'monthly',
      no_zero_invoices: !!f.no_zero_invoices,
      quantity: f.quantity === '' || f.quantity == null ? null : parseInt(f.quantity, 10),
      from_month: f.from_month === '' || f.from_month == null ? null : parseInt(f.from_month, 10),
      adult_fee: f.adult_fee === '' || f.adult_fee == null ? 0 : parseFloat(f.adult_fee) || 0,
      adult_from_age: f.adult_from_age === '' || f.adult_from_age == null ? 18 : parseInt(f.adult_from_age, 10) || 18,
      valid_from: f.valid_from || todayIso(),
      valid_until: f.valid_until || null,
    };
    try {
      if (f.id) {
        await api.patch(`/tariffs-discounts/tariffs/${selectedId}/fees/${f.id}`, payload);
        toast.success('Gebühr aktualisiert');
      } else {
        await api.post(`/tariffs-discounts/tariffs/${selectedId}/fees`, payload);
        toast.success('Gebühr angelegt — vorherige offene Zeile wurde geschlossen');
      }
      // Dauer gehört zum Tarif (Unterrichtsform), nicht zur Gebührenzeile
      const durRaw = f.duration_minutes;
      const durationMinutes = durRaw === '' || durRaw == null ? null : parseInt(durRaw, 10);
      await api.patch(`/tariffs-discounts/tariffs/${selectedId}/settings`, {
        duration_minutes: Number.isFinite(durationMinutes) && durationMinutes > 0 ? durationMinutes : null,
      });
      setFeeEditor(null);
      await reloadDetail();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const confirmDeleteFee = async () => {
    try {
      await api.delete(`/tariffs-discounts/tariffs/${selectedId}/fees/${feeToDelete.id}`);
      toast.success('Gebühreneintrag gelöscht');
      setFeeToDelete(null);
      await reloadDetail();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  const openPreview = async (asOfArg, focusOverride) => {
    if (!selectedId || !detail) return;
    const asOf = (asOfArg || previewAsOf || new Date().toISOString().slice(0, 10)).slice(0, 10);
    setPreviewAsOf(asOf);
    setPreviewError(null);
    const local = buildLocalPreview(detail, asOf);
    setPreview(local);
    setPreviewOpen(true);
    setPreviewLoading(true);
    const focus = focusOverride || simFocus;
    try {
      let r;
      if (simEnabled) {
        r = await api.post(`/tariffs-discounts/tariffs/${selectedId}/preview`, {
          as_of: asOf,
          focus_participant: focus.participant,
          focus_subject: focus.subject,
          gkz: simGkz || 'GRE',
          bildungsgutschein: simVoucher,
          participants: simParticipants,
        });
      } else {
        r = await api.get(`/tariffs-discounts/tariffs/${selectedId}/preview`, {
          params: { as_of: asOf },
        });
      }
      if (r.data?.youth || r.data?.simulation) {
        setPreview(r.data);
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Server-Vorschau nicht verfügbar';
      setPreviewError(typeof msg === 'string' ? msg : 'Server-Vorschau nicht verfügbar');
    } finally {
      setPreviewLoading(false);
    }
  };

  const setSimSubject = (ti, fi, on) => {
    setSimParticipants((prev) => prev.map((p, i) => {
      if (i !== ti) return p;
      const subjects = [...p.subjects];
      subjects[fi] = on;
      return { ...p, subjects };
    }));
  };

  const setSimAge = (ti, age) => {
    setSimParticipants((prev) => prev.map((p, i) => (i === ti ? { ...p, age } : p)));
  };

  const fees = detail?.fees || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4 min-h-[60vh]" data-testid="tariff-master-detail">
      {/* LEFT: list */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col overflow-hidden">
        <div className="p-3 border-b border-slate-100 space-y-2">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-slate-900 flex-1">Tarife</h2>
            {zeroCount > 0 && (
              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">
                <AlertTriangle className="w-3 h-3" /> {zeroCount}× 0 €
              </span>
            )}
            <Button size="sm" variant="ghost" onClick={() => setTariffEditor({ ...EMPTY_TARIFF })} data-testid="add-tariff-button">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <Input
            placeholder="Suche…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="h-8 text-sm"
            data-testid="tariff-filter"
          />
          <label className="text-xs flex items-center gap-1.5 cursor-pointer text-slate-600">
            <input type="checkbox" checked={zeroOnly} onChange={(e) => setZeroOnly(e.target.checked)} data-testid="zero-only" />
            Nur 0 €
          </label>
        </div>
        <div className="flex-1 overflow-y-auto max-h-[70vh] divide-y" data-testid="tariff-list">
          {loading && <p className="p-4 text-sm text-slate-500">Laden…</p>}
          {!loading && filtered.length === 0 && (
            <p className="p-4 text-sm text-slate-500 text-center">Keine Tarife</p>
          )}
          {filtered.map((t) => {
            const isZero = parseFloat(t.amount) <= 0;
            const active = selectedId === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setSelectedId(t.id)}
                data-testid={`tariff-${t.id}`}
                className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 transition-colors ${
                  active ? 'bg-brand-red/5 border-l-4 border-brand-red' : 'border-l-4 border-transparent'
                }`}
              >
                <div className="flex items-start gap-2">
                  <div className={`mt-0.5 w-7 h-7 shrink-0 rounded-full flex items-center justify-center ${isZero ? 'bg-amber-200' : 'bg-slate-100'}`}>
                    <DollarSign className={`w-3.5 h-3.5 ${isZero ? 'text-amber-700' : 'text-slate-600'}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-900 truncate">{tariffFormName(t.name)}</p>
                    <p className={`text-xs font-semibold ${isZero ? 'text-amber-700' : 'text-slate-600'}`}>
                      {fmtMoney(t.amount)}
                      {!t.is_active && <span className="ml-1.5 text-slate-400">inaktiv</span>}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* RIGHT: detail */}
      <div className="space-y-4">
        {!selectedId && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-10 text-center text-sm text-slate-400">
            Tarif in der Liste auswählen
          </div>
        )}
        {selectedId && detailLoading && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-10 flex justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-red" />
          </div>
        )}
        {selectedId && !detailLoading && detail && (
          <>
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex items-start justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold text-slate-900" data-testid="tariff-detail-name">{detail.name}</h2>
                <p className="text-sm text-slate-500 mt-0.5">
                  Aktuell {fmtMoney(detail.amount)} · {billingLabel(detail.billing_type)}
                  {detail.duration_minutes ? ` · ${detail.duration_minutes} Min.` : ''}
                  {detail.gema_applicable && (
                    <span className="ml-2 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-brand-red/10 text-brand-red font-semibold">
                      <Music className="w-3 h-3" /> GEMA
                    </span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openPreview()}
                  data-testid="tariff-preview-button"
                >
                  <Calculator className="w-4 h-4 mr-1" /> Vorschau
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => setTariffToDelete(detail)}
                  data-testid={`delete-tariff-${detail.id}`}
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Löschen
                </Button>
              </div>
            </div>

            {/* Gebührenhistorie */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="tariff-fee-history">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-900">Gebührenhistorie</h3>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setFeeEditor({
                    ...EMPTY_FEE,
                    amount: String(detail.amount ?? ''),
                    billing_type: detail.billing_type || 'monthly',
                    adult_fee: detail.adult_surcharge_amount != null ? String(detail.adult_surcharge_amount) : '',
                    duration_minutes: detail.duration_minutes != null
                      ? String(detail.duration_minutes)
                      : ((detail.name || '').match(/(\d{2,3})\s*Min/i)?.[1] || ''),
                  })}
                  data-testid="add-fee-button"
                >
                  <Plus className="w-4 h-4 mr-1" /> Neue Gebühr
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs text-slate-500 uppercase tracking-wide">
                    <tr>
                      <th className="px-3 py-2 font-semibold">Gebühr</th>
                      <th className="px-3 py-2 font-semibold">Turnus</th>
                      <th className="px-3 py-2 font-semibold">keine Nullrechn.</th>
                      <th className="px-3 py-2 font-semibold">Anzahl</th>
                      <th className="px-3 py-2 font-semibold">Ab Monat</th>
                      <th className="px-3 py-2 font-semibold">Erw. Gebühr</th>
                      <th className="px-3 py-2 font-semibold">ab Alter</th>
                      <th className="px-3 py-2 font-semibold">Gültig ab</th>
                      <th className="px-3 py-2 font-semibold">bis</th>
                      <th className="px-3 py-2 font-semibold w-20" />
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {fees.length === 0 && (
                      <tr>
                        <td colSpan={10} className="px-3 py-6 text-center text-slate-400">
                          Keine Gebühreneinträge
                        </td>
                      </tr>
                    )}
                    {fees.map((fee) => (
                      <tr key={fee.id} className="hover:bg-slate-50" data-testid={`fee-row-${fee.id}`}>
                        <td className="px-3 py-2 font-semibold tabular-nums">{fmtMoney(fee.amount)}</td>
                        <td className="px-3 py-2">{billingLabel(fee.billing_type)}</td>
                        <td className="px-3 py-2">{fee.no_zero_invoices ? '✓' : ''}</td>
                        <td className="px-3 py-2">{fee.quantity ?? ''}</td>
                        <td className="px-3 py-2">{fee.from_month ?? ''}</td>
                        <td className="px-3 py-2 tabular-nums">
                          {(() => {
                            const total = parseFloat(
                              fee.adult_total_fee
                              ?? ((parseFloat(fee.amount) || 0)
                                + (parseFloat(fee.adult_fee_effective ?? fee.adult_fee) || 0)),
                            ) || 0;
                            const surcharge = parseFloat(fee.adult_fee_effective ?? fee.adult_fee) || 0;
                            if (total <= 0) return '';
                            return (
                              <span className="inline-flex items-center gap-1" title={
                                surcharge > 0
                                  ? `Grundgebühr ${fmtMoney(fee.amount)} + Zuschlag ${fmtMoney(surcharge)}`
                                  : undefined
                              }>
                                {fmtMoney(total)}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-3 py-2">{fee.adult_from_age_effective ?? fee.adult_from_age ?? ''}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{fmtDate(fee.valid_from)}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{fmtDate(fee.valid_until)}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-0.5">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setFeeEditor({
                                ...fee,
                                amount: String(fee.amount),
                                adult_fee: fee.adult_fee != null ? String(fee.adult_fee) : '',
                                adult_from_age: fee.adult_from_age != null ? String(fee.adult_from_age) : '18',
                                quantity: fee.quantity != null ? String(fee.quantity) : '',
                                from_month: fee.from_month != null ? String(fee.from_month) : '',
                                valid_from: fee.valid_from || '',
                                valid_until: fee.valid_until || '',
                                duration_minutes: detail?.duration_minutes != null
                                  ? String(detail.duration_minutes)
                                  : ((detail?.name || '').match(/(\d{2,3})\s*Min/i)?.[1] || ''),
                              })}
                              data-testid={`edit-fee-${fee.id}`}
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => setFeeToDelete(fee)}
                              data-testid={`delete-fee-${fee.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Tarifeinstellungen */}
            {settings && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 space-y-4" data-testid="tariff-settings">
                <h3 className="text-sm font-semibold text-slate-900">Tarifeinstellungen</h3>
                <p className="text-xs text-slate-500">
                  Gilt fachübergreifend für alle Fächer. Grundgebühr in der Historie;
                  Erwachsenenzuschlag steht in der Gebührenhistorie.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Name *</Label>
                    <Input
                      value={settings.name}
                      onChange={(e) => setSettings({ ...settings, name: e.target.value })}
                      data-testid="settings-name"
                    />
                  </div>
                  <div className="flex items-end gap-6 pb-1">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <Switch
                        checked={!!settings.is_active}
                        onCheckedChange={(v) => setSettings({ ...settings, is_active: v })}
                        data-testid="settings-active"
                      />
                      Aktiv
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <Switch
                        checked={!!settings.gema_applicable}
                        onCheckedChange={(v) => setSettings({ ...settings, gema_applicable: v })}
                        data-testid="settings-gema"
                      />
                      GEMA-Kopierlizenz
                    </label>
                  </div>
                  <div className="md:col-span-2">
                    <Label>Beschreibung</Label>
                    <Textarea
                      rows={2}
                      value={settings.description}
                      onChange={(e) => setSettings({ ...settings, description: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Sozialfonds (€/Mo, rabattfrei)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={settings.social_fund_amount}
                      onChange={(e) => setSettings({ ...settings, social_fund_amount: e.target.value })}
                      data-testid="settings-social-fund"
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button onClick={saveSettings} disabled={savingSettings} data-testid="save-settings-button">
                    Einstellungen speichern
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* New tariff dialog */}
      <Dialog open={!!tariffEditor} onOpenChange={(o) => { if (!o) setTariffEditor(null); }}>
        <DialogContent data-testid="tariff-editor">
          <DialogHeader>
            <DialogTitle>Neuer Tarif</DialogTitle>
          </DialogHeader>
          {tariffEditor && (
            <div className="space-y-3 mt-1">
              <div>
                <Label>Name *</Label>
                <Input
                  value={tariffEditor.name}
                  onChange={(e) => setTariffEditor({ ...tariffEditor, name: e.target.value })}
                  data-testid="tariff-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Startgebühr (€) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={tariffEditor.amount}
                    onChange={(e) => setTariffEditor({ ...tariffEditor, amount: e.target.value })}
                    data-testid="tariff-amount-input"
                  />
                </div>
                <div>
                  <Label>Turnus</Label>
                  <select
                    className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm"
                    value={tariffEditor.billing_type || 'monthly'}
                    onChange={(e) => setTariffEditor({ ...tariffEditor, billing_type: e.target.value })}
                  >
                    {BILLING_TYPES.map((b) => (
                      <option key={b.value} value={b.value}>{b.label}</option>
                    ))}
                  </select>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Rechnungsrhythmus. 14-tägig<em>er Unterricht</em> wird am Kurs (Turnus 14) gesetzt;
                    die Abrechnung nutzt den Monats-Tarif (50&nbsp;% + 2,50&nbsp;€).
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Erwachsenenzuschlag (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={tariffEditor.adult_surcharge_amount}
                    onChange={(e) => setTariffEditor({ ...tariffEditor, adult_surcharge_amount: e.target.value })}
                    data-testid="tariff-adult-surcharge-input"
                  />
                </div>
                <div>
                  <Label>Sozialfonds (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={tariffEditor.social_fund_amount}
                    onChange={(e) => setTariffEditor({ ...tariffEditor, social_fund_amount: e.target.value })}
                    data-testid="tariff-social-fund-input"
                  />
                </div>
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm">
                  <Switch
                    checked={!!tariffEditor.is_active}
                    onCheckedChange={(v) => setTariffEditor({ ...tariffEditor, is_active: v })}
                    data-testid="tariff-active-switch"
                  />
                  Aktiv
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Switch
                    checked={!!tariffEditor.gema_applicable}
                    onCheckedChange={(v) => setTariffEditor({ ...tariffEditor, gema_applicable: v })}
                    data-testid="tariff-gema-switch"
                  />
                  GEMA
                </label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTariffEditor(null)}>Abbrechen</Button>
            <Button onClick={saveNewTariff} data-testid="save-tariff-button">Anlegen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fee editor */}
      <Dialog open={!!feeEditor} onOpenChange={(o) => { if (!o) setFeeEditor(null); }}>
        <DialogContent className="max-w-lg" data-testid="fee-editor">
          <DialogHeader>
            <DialogTitle>{feeEditor?.id ? 'Gebühr bearbeiten' : 'Neue Gebühr'}</DialogTitle>
          </DialogHeader>
          {feeEditor && (
            <form
              className="space-y-3 mt-1"
              onSubmit={(e) => {
                e.preventDefault();
                saveFee();
              }}
            >
              {!feeEditor.id && (
                <p className="text-xs text-slate-500 bg-slate-50 rounded-md p-2">
                  Beim Anlegen mit offenem Ende wird die bisherige offene Zeile automatisch mit
                  „bis = Tag vor Gültig ab“ geschlossen.
                </p>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Gebühr (€) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={feeEditor.amount}
                    onChange={(e) => setFeeEditor({ ...feeEditor, amount: e.target.value })}
                    data-testid="fee-amount-input"
                  />
                </div>
                <div>
                  <Label>Turnus</Label>
                  <select
                    className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm"
                    value={feeEditor.billing_type || 'monthly'}
                    onChange={(e) => setFeeEditor({ ...feeEditor, billing_type: e.target.value })}
                    data-testid="fee-billing-type"
                  >
                    {BILLING_TYPES.map((b) => (
                      <option key={b.value} value={b.value}>{b.label}</option>
                    ))}
                  </select>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Rechnungsrhythmus. 14-tägiger Unterricht → Kurs-Turnus 14; Monats-Tarif × 50&nbsp;% + 2,50&nbsp;€.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Dauer (Minuten)</Label>
                  <Input
                    type="number"
                    min="5"
                    max="180"
                    step="5"
                    placeholder="z. B. 30"
                    value={feeEditor.duration_minutes ?? ''}
                    onChange={(e) => setFeeEditor({ ...feeEditor, duration_minutes: e.target.value })}
                    data-testid="fee-duration-minutes"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Unterrichtsdauer der Form — wird bei Belegungen automatisch übernommen.
                  </p>
                </div>
                <div />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Gültig ab *</Label>
                  <DateInput
                    value={feeEditor.valid_from || ''}
                    onChange={(e) => setFeeEditor({ ...feeEditor, valid_from: e.target.value })}
                    placeholder={feeEditor.id ? 'TT.MM.JJJJ' : fmtDate(todayIso())}
                    onEnterCommit={() => saveFeeBtnRef.current?.focus()}
                    data-testid="fee-valid-from"
                  />
                </div>
                <div>
                  <Label>bis</Label>
                  <DateInput
                    value={feeEditor.valid_until || ''}
                    onChange={(e) => setFeeEditor({ ...feeEditor, valid_until: e.target.value })}
                    placeholder="TT.MM.JJJJ"
                    onEnterCommit={() => saveFeeBtnRef.current?.focus()}
                    data-testid="fee-valid-until"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Erwachsene → Zuschlag (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={feeEditor.adult_fee}
                    onChange={(e) => setFeeEditor({ ...feeEditor, adult_fee: e.target.value })}
                    data-testid="fee-adult-fee"
                  />
                </div>
                <div>
                  <Label>Erwachsene → ab Alter</Label>
                  <Input
                    type="number"
                    min="0"
                    value={feeEditor.adult_from_age}
                    onChange={(e) => setFeeEditor({ ...feeEditor, adult_from_age: e.target.value })}
                    data-testid="fee-adult-from-age"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Anzahl</Label>
                  <Input
                    type="number"
                    min="0"
                    value={feeEditor.quantity}
                    onChange={(e) => setFeeEditor({ ...feeEditor, quantity: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Ab Monat (1–12)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="12"
                    value={feeEditor.from_month}
                    onChange={(e) => setFeeEditor({ ...feeEditor, from_month: e.target.value })}
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Switch
                  checked={!!feeEditor.no_zero_invoices}
                  onCheckedChange={(v) => setFeeEditor({ ...feeEditor, no_zero_invoices: v })}
                  data-testid="fee-no-zero"
                />
                keine Nullrechnungen
              </label>
              <DialogFooter className="px-0">
                <Button type="button" variant="outline" onClick={() => setFeeEditor(null)}>Abbrechen</Button>
                <Button ref={saveFeeBtnRef} type="submit" data-testid="save-fee-button">Speichern</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!tariffToDelete} onOpenChange={(o) => { if (!o) setTariffToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Tarif löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{tariffToDelete?.name}</strong> und seine Gebührenhistorie werden unwiderruflich gelöscht.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteTariff} data-testid="confirm-delete-tariff" className="bg-red-600 hover:bg-red-700">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!feeToDelete} onOpenChange={(o) => { if (!o) setFeeToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Gebühreneintrag löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Der Eintrag ab {fmtDate(feeToDelete?.valid_from)} ({fmtMoney(feeToDelete?.amount)}) wird entfernt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteFee} className="bg-red-600 hover:bg-red-700">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={previewOpen}
        onOpenChange={(o) => {
          setPreviewOpen(o);
          if (!o) {
            setPreview(null);
            setPreviewError(null);
          }
        }}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="tariff-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-brand-red" />
              Tarifvorschau{preview?.tariff_name ? `: ${preview.tariff_name}` : detail?.name ? `: ${detail.name}` : ''}
            </DialogTitle>
            <DialogDescription>
              Basisvorschau: Gebühr und feste/altersabhängige Zu-/Abschläge.
              Optional: anonymes Test-Szenario (Tln. × Fächer) für Familien- und Mehrfächerermäßigung.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap items-end gap-3 py-1">
            <div>
              <Label className="text-xs">Stichtag</Label>
              <DateInput
                value={previewAsOf}
                onChange={(e) => setPreviewAsOf(e.target.value)}
                data-testid="tariff-preview-as-of"
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => openPreview(previewAsOf)}
              disabled={previewLoading}
              data-testid="tariff-preview-refresh"
            >
              {previewLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aktualisieren'}
            </Button>
            {preview && (
              <p className="text-xs text-slate-500 pb-2">
                {preview.billing_type_label}
                {preview.fee?.valid_from && ` · Gebühr ab ${fmtDate(preview.fee.valid_from)}`}
                {preview.fee?.valid_until && ` bis ${fmtDate(preview.fee.valid_until)}`}
              </p>
            )}
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-3 space-y-3" data-testid="tariff-preview-sim">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-800">Test-Szenario (ohne Namen)</p>
                <p className="text-[11px] text-slate-500">
                  Zuerst Mehrfächerermäßigung pro Tln., dann Familienrang nach höchster Zwischensumme
                  (Rang 1 ohne Familienermäßigung). Staffel ab Rang 2: −10/−20/−30/−40&nbsp;%.
                  Erwachsenenzuschlag wird mitermäßigt, nur GEMA bleibt rabattfrei.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs">Szenario</Label>
                <Switch checked={simEnabled} onCheckedChange={setSimEnabled} data-testid="tariff-preview-sim-toggle" />
              </div>
            </div>

            {simEnabled && (
              <>
                <div className="overflow-x-auto border border-slate-200 rounded-md bg-white">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                      <tr>
                        <th className="text-left px-2 py-1.5">Teilnehmer</th>
                        <th className="text-left px-2 py-1.5 w-16">Alter</th>
                        {[1, 2, 3, 4].map((f) => (
                          <th key={f} className="text-center px-2 py-1.5">Fach {f}</th>
                        ))}
                        <th className="text-center px-2 py-1.5">Fokus</th>
                      </tr>
                    </thead>
                    <tbody>
                      {simParticipants.map((p, ti) => (
                        <tr key={ti} className="border-t border-slate-100">
                          <td className="px-2 py-1.5 font-medium">Tln. {ti + 1}</td>
                          <td className="px-2 py-1.5">
                            <Input
                              type="number"
                              min={1}
                              max={99}
                              className="h-7 w-14 text-xs"
                              value={p.age}
                              onChange={(e) => setSimAge(ti, Number(e.target.value) || 1)}
                            />
                          </td>
                          {p.subjects.map((on, fi) => (
                            <td key={fi} className="px-2 py-1.5 text-center">
                              <Checkbox
                                checked={on}
                                onCheckedChange={(v) => setSimSubject(ti, fi, Boolean(v))}
                              />
                            </td>
                          ))}
                          <td className="px-2 py-1.5 text-center">
                            <input
                              type="radio"
                              name="sim-focus"
                              checked={simFocus.participant === ti + 1}
                              onChange={() => {
                                const firstFach = p.subjects.findIndex(Boolean) + 1 || 1;
                                setSimFocus({ participant: ti + 1, subject: firstFach });
                              }}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <Label className="text-xs">Fokus Fach</Label>
                    <select
                      className="h-8 rounded-md border border-slate-300 text-xs px-2"
                      value={simFocus.subject}
                      onChange={(e) => setSimFocus((f) => ({ ...f, subject: Number(e.target.value) }))}
                    >
                      {[1, 2, 3, 4].map((f) => (
                        <option key={f} value={f}>Fach {f}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">GKZ</Label>
                    <Input className="h-8 w-20 text-xs" value={simGkz} onChange={(e) => setSimGkz(e.target.value)} />
                  </div>
                  <label className="flex items-center gap-2 text-xs pb-1.5">
                    <Checkbox checked={simVoucher} onCheckedChange={(v) => setSimVoucher(Boolean(v))} />
                    Bildungsgutschein aktiv
                  </label>
                  <Button size="sm" className="bg-brand-red hover:bg-brand-red/90" onClick={() => openPreview(previewAsOf)} disabled={previewLoading}>
                    Szenario berechnen
                  </Button>
                </div>
              </>
            )}
          </div>

          {previewError && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              {previewError} — Anzeige aus Tarifdaten.
            </p>
          )}

          {preview ? (
            <div className="space-y-4">
              {preview.simulation?.notes?.length > 0 && (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 space-y-1">
                  {preview.simulation.notes.map((n, i) => <p key={i}>{n}</p>)}
                </div>
              )}

              {preview.simulation?.cells?.length > 0 && (
                <div className="rounded-lg border border-slate-200 overflow-hidden">
                  <div className="px-3 py-2 bg-slate-50 text-xs font-semibold text-slate-700 flex flex-wrap justify-between gap-x-3 gap-y-1">
                    <span>
                      Alle Belegungen im Szenario
                      {preview.simulation.base_fee != null && (
                        <span className="font-normal text-slate-500">
                          {' '}· Grundgebühr {Number(preview.simulation.base_fee).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                        </span>
                      )}
                    </span>
                    <span>Familien-Summe: {Number(preview.simulation.family_total).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</span>
                  </div>
                  <table className="w-full text-xs">
                    <thead className="text-[10px] uppercase text-slate-500 border-t border-slate-100">
                      <tr>
                        <th className="text-left px-3 py-1.5">Belegung</th>
                        <th className="text-right px-2 py-1.5">Tln-Rang</th>
                        <th className="text-right px-2 py-1.5">Fam. %</th>
                        <th className="text-right px-2 py-1.5">Fach-Rang</th>
                        <th className="text-right px-2 py-1.5">MFE. %</th>
                        <th className="text-right px-3 py-1.5">Monatsbetrag</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.simulation.cells.map((c) => {
                        const isFocus = c.participant === preview.simulation.focus_participant
                          && c.subject === preview.simulation.focus_subject;
                        const fmtPctEur = (pct, amt) => {
                          const p = Number(pct) || 0;
                          const a = Number(amt) || 0;
                          if (p <= 0) return '0 %';
                          const eur = a.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                          return `−${Math.round(p)} % / ${eur} €`;
                        };
                        return (
                          <tr
                            key={`${c.participant}-${c.subject}`}
                            className={`border-t border-slate-100 cursor-pointer ${isFocus ? 'bg-amber-50' : 'hover:bg-slate-50'}`}
                            onClick={() => {
                              const next = { participant: c.participant, subject: c.subject };
                              setSimFocus(next);
                              openPreview(previewAsOf, next);
                            }}
                          >
                            <td className="px-3 py-1.5">Tln. {c.participant} · Fach {c.subject} (Alter {c.age})</td>
                            <td className="px-2 py-1.5 text-right tabular-nums">{c.konto_rank}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap text-emerald-800">
                              {fmtPctEur(c.fam_pct, c.fam_amount)}
                            </td>
                            <td className="px-2 py-1.5 text-right tabular-nums">{c.fach_rank}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap text-emerald-800">
                              {fmtPctEur(c.mfe_pct, c.mfe_amount)}
                            </td>
                            <td className="px-3 py-1.5 text-right tabular-nums font-medium">
                              {Number(c.total).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <div className={`grid grid-cols-1 ${preview.adult ? 'md:grid-cols-2' : ''} gap-4`}>
                {preview.youth && <PreviewScenario scenario={preview.youth} />}
                {preview.adult && <PreviewScenario scenario={preview.adult} />}
              </div>
            </div>
          ) : (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-brand-red" />
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>Schließen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
